import { Box, Typography } from "@mui/material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { BOTTOM_NAV, FONT_SIZE, INK, LAYOUT_OFFSET } from "../../../../theme/tokens";
import { originFilterNode } from "../../../RequestData/RequestOrigin/originFilterNode";
import { RequestOriginFilterChips } from "../../../RequestData/RequestOrigin/RequestOriginFilterChips";
import type { OriginFilterState } from "../../../RequestData/RequestOrigin/requestOriginMeta";
import { FilterFieldRow } from "../../../UIToolkit/Filters/FilterFieldRow";
import { RoleGroupOrJobSelector } from "../../../UIToolkit/molecules/RoleGroupOrJobSelector";
import { ShiftTypeFilterChips, type ShiftTypeFilterValue } from "../../../UIToolkit/molecules/ShiftTypeFilterChips";
import { InHouseMobileSheet } from "../../../UIToolkit/Popups/InHouseMobileSheet";
import SystemButton from "../../../UIToolkit/SystemButton/SystemButton";

/** One of the Requests screen's filters, as `RequestsTab` owns it: the value and its setter. */
export type GapsRequestsFilterControl<T> = { selected: T; onChange: (next: T) => void };

type Props = {
	open: boolean;
	onClose: () => void;
	roleGroupFilter: GapsRequestsFilterControl<string[]>;
	jobFilter: GapsRequestsFilterControl<string[]>;
	shiftTypeFilter: GapsRequestsFilterControl<ShiftTypeFilterValue>;
	/** The Origin row: its chips once origins are in, a skeleton while they load, no row where they are unavailable or failed. */
	originFilter: OriginFilterState;
	onClear: () => void;
};

/**
 * The Requests screen's own filters, moved behind a button (P3-12): the job or role-group picker and Day / Night —
 * exactly what `RequestsTabFilters` offers above the classic list, with the same components and the same state, so
 * the two layouts narrow identically. No Day / Evening / Night split: the artifact's is not a filter the app has.
 *
 * The home's filter sheet's shape — header, sized to its content, stopping above the bar — so the two filters on the
 * gaps view read as one control.
 */
export const GapsOpenShiftsFilterSheet: FC<Props> = ({ open, onClose, roleGroupFilter, jobFilter, shiftTypeFilter, originFilter, onClear }) => {
	const { t } = useTranslation();
	const origin = originFilterNode(originFilter, (control) => <RequestOriginFilterChips control={control} />);

	const footer = (
		<Box sx={{ display: "flex", gap: 1 }}>
			<SystemButton buttonVariant="secondary" size="compact" onClick={onClear} data-testid="gaps-requests-filter-clear" sx={{ flex: 1 }}>
				{t("gaps.requests.filterClear")}
			</SystemButton>
			<SystemButton buttonVariant="primary" size="compact" onClick={onClose} data-testid="gaps-requests-filter-done" sx={{ flex: 1 }}>
				{t("gaps.filter.done")}
			</SystemButton>
		</Box>
	);

	return (
		<InHouseMobileSheet
			open={open}
			onClose={onClose}
			title={t("gaps.requests.filterTitle")}
			showHeader
			// Two controls and no list: nothing in it scrolls, so the swipe that closes the home's filter closes this one too.
			swipeToClose
			showHandle
			footer={footer}
			fitContent
			maxHeight={`calc(100dvh - ${BOTTOM_NAV.TOTAL_HEIGHT_CSS} - ${LAYOUT_OFFSET.SHEET_TOP_GAP_CSS})`}
			bottomOffset={BOTTOM_NAV.TOTAL_HEIGHT_CSS}
			dataTestId="gaps-requests-filter-sheet"
		>
			<Box data-testid="gaps-requests-filter-sheet-body" sx={{ display: "flex", flexDirection: "column", gap: 2, pb: 1 }}>
				<Typography sx={{ fontSize: FONT_SIZE.CAPTION, color: INK.MUTED }}>{t("gaps.requests.filterSubtitle")}</Typography>
				<Box data-testid="gaps-requests-filter-job">
					<RoleGroupOrJobSelector
						fullWidth
						selectedRoleGroups={roleGroupFilter.selected}
						onSelectedRoleGroupsChanged={roleGroupFilter.onChange}
						selectedJobIds={jobFilter.selected}
						onSelectedJobsChanged={jobFilter.onChange}
					/>
				</Box>
				<FilterFieldRow label={t("gaps.requests.filterShiftType")} dataTestId="gaps-requests-filter-shift-type">
					<ShiftTypeFilterChips value={shiftTypeFilter.selected} onChange={shiftTypeFilter.onChange} dataTestId="gaps-requests-shift-type-chips" />
				</FilterFieldRow>
				{origin !== null && (
					<FilterFieldRow label={t("request.origin.filter.label")} dataTestId="gaps-requests-filter-origin">
						{origin}
					</FilterFieldRow>
				)}
			</Box>
		</InHouseMobileSheet>
	);
};
